import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Chip, Avatar, Paper, CircularProgress, Dialog, TextField, Popover } from '@mui/material';
import { ThumbUp, ThumbDown, CheckCircle } from '@mui/icons-material';
import AIIcon from './AIIcon';
import CandidateProfileSidebar from './CandidateProfileSidebar';
import LoadingCandidateCard from './LoadingCandidateCard';
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue';
import { copilotSearchEditForAttr } from '../utils/api';
import { unescape } from 'lodash';
import { parseICPEditResponse, mapAttributeNameToSectionId } from '../types/api';

/**
 * ReviewMatchesTabV2 - Queue-Aware Version
 * 
 * This version is properly integrated with the centralized discovery queue system:
 * - Uses handleRefreshResultsFromQueue for queue-aware API calls
 * - Doesn't trigger legacy fetchMatches automatically 
 * - Integrates candidate feedback through the queue system
 * - No conflicting sessionStorage or legacy state management
 */
const ReviewMatchesTabV2 = ({ 
  userId,
  profiles, 
  macroMappingData,
  onCandidateApproval,
  handleRefreshResultsFromQueue, // Queue-aware refresh function
  fetchMatches, // Queue-aware fetch function for candidate profiles
  setMessages, // For adding feedback messages
  onRefreshResults // For triggering search refresh with feedback
}) => {
  // Core state
  const [approvedCandidates, setApprovedCandidates] = useState(new Set());
  const [rejectedCandidates, setRejectedCandidates] = useState(new Set());
  
  // Candidate Profile Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  // Candidate pagination state
  const [currentCandidateOffset, setCurrentCandidateOffset] = useState(0);

  // Loading state for fetching profiles
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);

  // Thumbs down modal state
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');
  const [selectedCandidateForReason, setSelectedCandidateForReason] = useState(null);
  
  // Enhanced feedback modal state
  const [icpEditResponse, setIcpEditResponse] = useState(null);
  const [selectedChips, setSelectedChips] = useState({
    additions: new Set(),
    removals: new Set(), 
    suggestions: new Set()
  });
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);

  // Popover state for matching criteria
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [popoverCriteria, setPopoverCriteria] = useState([]);

  // Queue-aware profile fetching - only when explicitly needed
  const handleFetchProfiles = useCallback(async () => {
    // Check if we have completed queue items first
    const allQueueItems = centralDiscoveryQueue.getAllItems();
    const completedItems = allQueueItems.filter(item => item.disco_done === true);
    
    if (completedItems.length === 0) {
      console.log('ReviewMatchesTabV2: No completed queue items - cannot fetch profiles');
      return;
    }

    console.log('ReviewMatchesTabV2: Fetching profiles using queue-aware approach');
    setIsLoadingProfiles(true);
    
    try {
      // First: Get match counts using queue-aware refresh function
      await handleRefreshResultsFromQueue(completedItems);
      console.log('ReviewMatchesTabV2: Match counts updated via queue system');
      
      // Second: Fetch actual candidate profiles using queue-aware fetchMatches
      if (fetchMatches) {
        console.log('ReviewMatchesTabV2: Calling fetchMatches with completed queue items');
        await fetchMatches(completedItems); // ✅ Pass queue items to use centralized queue approach
        console.log('ReviewMatchesTabV2: Candidate profiles fetched via queue system');
      }
    } catch (error) {
      console.error('ReviewMatchesTabV2: Error fetching profiles via queue system:', error);
    } finally {
      setIsLoadingProfiles(false);
    }
  }, [handleRefreshResultsFromQueue, fetchMatches]);

  // Auto-fetch profiles when switching to this tab and no profiles exist
  useEffect(() => {
    const shouldFetchProfiles = !profiles || (profiles && Object.keys(profiles).length === 0);
    
    if (shouldFetchProfiles && !isLoadingProfiles) {
      // Check if we have completed queue items first
      const allQueueItems = centralDiscoveryQueue.getAllItems();
      const completedItems = allQueueItems.filter(item => item.disco_done === true);
      
      if (completedItems.length > 0) {
        console.log('ReviewMatchesTabV2: Found completed queue items, auto-fetching profiles');
        handleFetchProfiles();
      } else {
        console.log('ReviewMatchesTabV2: No completed queue items, waiting for user interaction');
      }
    }
  }, [profiles, isLoadingProfiles, handleFetchProfiles]); // Include dependencies to react to changes

  // Listen for queue changes and auto-refresh profiles when queue items are updated
  useEffect(() => {
    console.log('🔄 ReviewMatchesTabV2: Setting up queue listener for profile refresh');
    
    const handleQueueUpdate = (queueItems) => {
      console.log('🔄 ReviewMatchesTabV2: Queue changed, received', queueItems?.length || 0, 'total items');
      
      // Only refresh if we already have profiles loaded (avoid initial load conflicts)
      if (!profiles || Object.keys(profiles).length === 0) {
        console.log('🔄 ReviewMatchesTabV2: No existing profiles, skipping auto-refresh');
        return;
      }
      
      const completedItems = queueItems.filter(item => item.disco_done === true);
      console.log('🔄 ReviewMatchesTabV2: Found', completedItems.length, 'completed items');
      
      if (completedItems.length > 0 && !isLoadingProfiles) {
        console.log('🔄 ReviewMatchesTabV2: Auto-refreshing profiles due to queue changes...');
        handleFetchProfiles().catch(error => {
          console.error('❌ ReviewMatchesTabV2: Error auto-refreshing profiles:', error);
        });
      }
    };

    // Listen for queue changes
    centralDiscoveryQueue.addListener(handleQueueUpdate);
    console.log('🔄 ReviewMatchesTabV2: Queue listener added');
    
    // Cleanup listener on unmount
    return () => {
      console.log('🔄 ReviewMatchesTabV2: Cleaning up queue listener');
      centralDiscoveryQueue.removeListener(handleQueueUpdate);
    };
  }, [profiles, isLoadingProfiles, handleFetchProfiles]); // Dependencies to react to state changes

  // Convert profiles object to array for easier handling
  const candidateList = profiles ? Object.entries(profiles).map(([id, profile]) => {
    // Extract data from the candidate object
    const name = profile?.candidate?.full_name ? decodeURIComponent(profile.candidate.full_name) : 'Unknown Candidate';
    
    // Get current title
    const title = profile?.workexp
      ?.find(exp => exp.duration?.to_present)
      ?.projects?.map(proj => proj.role_and_group?.title)
      .filter(Boolean)[0] || 'Unknown Title';
    
    // Get company name
    const company = profile?.workexp
      ?.find(exp => exp.duration?.to_present)
      ?.company?.name || 'Unknown Company';
    
    // Get location
    const city = profile?.candidate?.location?.city || '';
    const state = profile?.candidate?.location?.state || '';
    const country = profile?.candidate?.location?.country || '';
    
    // Format location as "City, State, Country"
    let location = '';
    if (city && state && country) {
      location = `${city}, ${state}, ${country}`;
    } else if (city && country) {
      location = `${city}, ${country}`;
    } else if (city) {
      location = city;
    } else {
      location = 'Unknown Location';
    }

    return {
      id,
      profile,
      name,
      title,
      company,
      location,
      first_name: name.split(' ')[0] || 'Unknown',
      last_name: name.split(' ').slice(1).join(' ') || '',
    };
  }) : [];

  const handleApproval = (candidateId, approved) => {
    const newApproved = new Set(approvedCandidates);
    const newRejected = new Set(rejectedCandidates);
    
    if (approved) {
      newApproved.add(candidateId);
      newRejected.delete(candidateId);
    } else {
      newRejected.add(candidateId);
      newApproved.delete(candidateId);
    }
    
    setApprovedCandidates(newApproved);
    setRejectedCandidates(newRejected);
    
    if (onCandidateApproval) {
      onCandidateApproval(candidateId, approved);
    }

    // Auto-navigate to messaging flow after 5 approvals
    if (newApproved.size === 5) {
      console.log('ReviewMatchesTabV2: 5 candidates approved, auto-navigating to messaging flow');
      setTimeout(() => {
        // Switch the content tab
        if (window.switchToMessagingTab && typeof window.switchToMessagingTab === 'function') {
          window.switchToMessagingTab();
        }
        
        // Also trigger the header navigation to update discovery state
        if (window.onNavigateToSendOutreach && typeof window.onNavigateToSendOutreach === 'function') {
          window.onNavigateToSendOutreach();
        }
      }, 500);
    }
  };

  // Handle candidate name click to open sidebar
  const handleCandidateNameClick = (candidate) => {
    setSelectedCandidate(candidate.profile);
    setSelectedCandidateId(candidate.id);
    setSidebarOpen(true);
  };

  // Handle sidebar close
  const handleSidebarClose = () => {
    setSidebarOpen(false);
    setSelectedCandidate(null);
    setSelectedCandidateId(null);
  };

  // Thumbs down handlers
  const handleThumbsDown = (candidate) => {
    setSelectedCandidateForReason(candidate);
    setShowReasonModal(true);
    
    // Set the candidate as rejected immediately for visual feedback
    handleApproval(candidate.id, false);
  };

  const handleReasonSubmit = async () => {
    if (reason.trim() && selectedCandidateForReason) {
      // Create user message and add it directly to the messages array
      const userMessage = {
        role: 'User',
        content: `I don't like candidate ${selectedCandidateForReason.name} because: ${reason.trim()}`,
        id: `user_candidate_feedback_${Date.now()}`
      };
      console.log('reason', reason);
      const allQueueItems = centralDiscoveryQueue.getAllItems();
      const completedItems = allQueueItems.filter(item => item.disco_done === true);
      const requirements = completedItems.map(item => ({
        section_type: item.section_type || 'Must Have',
        section_name: item.section_name,
        section_id: item.section_id,
        bool_str: item.bool_str || ''
      }));

      if(requirements.length > 0) {
        const data = await copilotSearchEditForAttr({
          reset_context: true,
          joid: userId,
          qna_search_edit_info: {
            verbose: false,
            icpid: '',
            questions: [reason],
            requirements: requirements || [],
          }
        });
        console.log('copilotSearchEditForAttr response:', data);
      }

      // Add message to the messages array first, then trigger refresh
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = [...prev, userMessage];
          
          // Trigger search refresh with the complete updated messages array
          if (onRefreshResults) {
            console.log('ReviewMatchesTabV2: Triggering onRefreshResults for candidate:', selectedCandidateForReason.name);
            console.log('ReviewMatchesTabV2: Sending full message history with', updatedMessages.length, 'messages');
            // Use setTimeout to ensure the state update is processed
            setTimeout(() => {
              onRefreshResults(updatedMessages);
            }, 0);
          } else {
            console.warn('ReviewMatchesTabV2: onRefreshResults callback not available');
          }
          
          return updatedMessages;
        });
      }
    }
    
    setShowReasonModal(false);
    setReason('');
    setSelectedCandidateForReason(null);
  };

  // Fetch ICP edit suggestions based on user feedback
  const fetchICPSuggestions = async (feedbackText) => {
    setIsLoadingFeedback(true);
    
    try {
      // Get requirements from queue
      const allQueueItems = centralDiscoveryQueue.getAllItems();
      const completedItems = allQueueItems.filter(item => item.disco_done === true);
      const requirements = completedItems.map(item => ({
        section_type: item.section_type || 'Must Have',
        section_name: item.section_name,
        section_id: item.section_id,
        bool_str: item.bool_str || '',
        attribute_name: item.attribute_name
      }));
      
      // Call API to get suggestions based on candidate rejection
      const apiResponse = await copilotSearchEditForAttr({
        reset_context: true,
        joid: userId,
        qna_search_edit_info: {
          verbose: false,
          icpid: '',
          questions: [feedbackText],
          requirements: requirements || [],
        }
      });
      
      if (apiResponse) {
        const parsedResponse = parseICPEditResponse(apiResponse);
        setIcpEditResponse(parsedResponse);
      }
    } catch (error) {
      console.error('Error getting ICP edit suggestions:', error);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  // Helper function to process attribute data for special cases
  const processAttributeData = (attributeName, attributeValue, locationDetails = null) => {
    let sectionId = attributeName;
    let boolStr = `${attributeName}: ${attributeValue}`;
    
    // Special handling for location
    if (attributeName === 'location') {
      sectionId = 'location';
      if (locationDetails && Array.isArray(locationDetails)) {
        // Transform location_details to the expected queue format
        const queueLocationObjects = locationDetails.map(loc => ({
          google_place_id: loc.google_place_id,
          address: loc.formatted_address,
          country: loc.location.country,
          geo: {
            center: {
              lat: loc.geometry.center.lat,
              lng: loc.geometry.center.lng
            },
            bounds: {
              northeast: {
                lat: loc.geometry.bounds.northeast.lat,
                lng: loc.geometry.bounds.northeast.lng
              },
              southwest: {
                lat: loc.geometry.bounds.southwest.lat,
                lng: loc.geometry.bounds.southwest.lng
              }
            }
          },
          distance_in_miles: 50,
          negate_match: false,
          isKms: false,
          location_query_type: "current",
          state: loc.location.state,
          city: loc.location.city
        }));
        
        boolStr = JSON.stringify(queueLocationObjects);
      } else {
        // Fallback to creating basic location object from attribute_value
        const locationObj = {
          google_place_id: null,
          address: attributeValue,
          country: null,
          geo: {
            center: { lat: null, lng: null },
            bounds: {
              northeast: { lat: null, lng: null },
              southwest: { lat: null, lng: null }
            }
          },
          distance_in_miles: 50,
          negate_match: false,
          isKms: false,
          location_query_type: "current",
          state: null,
          city: attributeValue
        };
        boolStr = JSON.stringify([locationObj]);
      }
    }
    
    // Special handling for skills
    if (attributeName === 'skills' || attributeName === 'skill') {
      sectionId = 'skills_mh';
      boolStr = `skills: ${attributeValue}`;
    }
    
    return { sectionId, boolStr };
  };

  const handleReasonCancel = () => {
    setShowReasonModal(false);
    setReason('');
    // Keep rejection status - thumbs down should stay red even if user cancels
    // The rejection was already set in handleThumbsDown for immediate visual feedback
    setSelectedCandidateForReason(null);
    setIcpEditResponse(null);
    setSelectedChips({ additions: new Set(), removals: new Set(), suggestions: new Set() });
  };
  
  // Chip selection handlers
  const handleChipToggle = (type, index) => {
    setSelectedChips(prev => {
      const newSet = new Set(prev[type]);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return { ...prev, [type]: newSet };
    });
  };
  
  // Enhanced submit handlers
  const handleUpdateAndContinue = async () => {
    if (reason.trim() && selectedCandidateForReason) {
      // First fetch suggestions if we haven't already
      if (!icpEditResponse) {
        await fetchICPSuggestions(reason.trim());
      }
      
      await processSelectedChips(false); // Don't refresh results
      handleReasonCancel();
    }
  };
  
  const handleUpdateAndRefresh = async () => {
    if (reason.trim() && selectedCandidateForReason) {
      // First fetch suggestions if we haven't already
      if (!icpEditResponse) {
        await fetchICPSuggestions(reason.trim());
      }
      
      await processSelectedChips(true); // Refresh results
      handleReasonCancel();
    }
  };
  
  const processSelectedChips = async (shouldRefreshResults) => {
    // Process selected chips and update requirements
    const { additions, removals, suggestions } = selectedChips;
    
    // Create feedback message
    const userMessage = {
      role: 'User',
      content: `I don't like candidate ${selectedCandidateForReason.name} because: ${reason.trim()}`,
      id: `user_candidate_feedback_${Date.now()}`
    };
    
    // Add feedback message to chat
    if (typeof setMessages === 'function') {
      setMessages(prev => [...prev, userMessage]);
    }
    
    // Apply selected changes to queue items and requirements
    if (icpEditResponse) {
      // Get existing queue items to check for updates vs new additions
      const existingItems = centralDiscoveryQueue.getAllItems();
      
      // Process additions
      additions.forEach(index => {
        const addition = icpEditResponse.additions[index];
        if (addition) {
          // Process attribute data for special cases (location, skills)
          const { sectionId: processedSectionId, boolStr: processedBoolStr } = processAttributeData(
            addition.attribute_name, 
            addition.attribute_value, 
            addition.location_details
          );
          
          const topic = centralDiscoveryQueue.inferTopicFromSectionId(addition.attribute_name) || 'requirements';
          
          // Check if there's an existing item with similar attribute_name or section_id
          const existingItem = existingItems.find(item => 
            (item.topic === topic && 
             (item.attribute_name === addition.attribute_name || 
              item.section_name === addition.attribute_name ||
              item.bool_str?.includes(addition.attribute_name))) ||
            item.section_id === processedSectionId
          );
          
          if (existingItem) {
            // Update existing item by merging bool_str
            const updatedBoolStr = centralDiscoveryQueue.mergeBoolStrings(
              existingItem.bool_str, 
              processedBoolStr
            );
            
            const updatedQueueItem = {
              ...existingItem,
              bool_str: updatedBoolStr,
              section_type: addition.must_have_qualifier ? 'Must Have' : 'Nice to Have',
              disco_done: true,
              updated_from_feedback: true,
              updated_at: Date.now()
            };
            
            centralDiscoveryQueue.addQueueItem(updatedQueueItem);
            console.log('Updated existing requirement from feedback:', updatedQueueItem);
          } else {
            // Create new item with processed section_id
            const queueId = `${topic}_${processedSectionId}_${Date.now()}_${index}`;
            
            const newQueueItem = {
              topic: topic,
              queue_id: queueId,
              section_id: processedSectionId,
              section_name: addition.attribute_name,
              section_type: addition.must_have_qualifier ? 'Must Have' : 'Nice to Have',
              bool_str: processedBoolStr,
              attribute_name: addition.attribute_name,
              attribute_value: addition.attribute_value,
              temporal_qualifier: addition.temporal_qualifier || '',
              disco_done: true,
              created_from_feedback: true,
              status: 'discovery_done'
            };
            
            centralDiscoveryQueue.addQueueItem(newQueueItem);
            console.log('Added new requirement from feedback:', newQueueItem);
          }
        }
      });
      
      // Process removals - find and remove matching queue items
      removals.forEach(index => {
        const removal = icpEditResponse.removals[index];
        if (removal) {
          // Find existing queue items that match this removal
          const allItems = centralDiscoveryQueue.getAllItems();
          const itemsToRemove = allItems.filter(item => 
            item.attribute_name === removal.attribute_name ||
            item.bool_str?.includes(removal.attribute_value) ||
            item.section_name?.includes(removal.attribute_name)
          );
          
          itemsToRemove.forEach(item => {
            const queueId = item.queue_id || item.section_id;
            centralDiscoveryQueue.removeQueueItem(queueId);
            console.log('Removed requirement from feedback:', item);
          });
        }
      });
      
      // Process suggestions (treat as additions)
      suggestions.forEach(index => {
        const suggestion = icpEditResponse.suggestions.actions[index];
        if (suggestion) {
          // Process attribute data for special cases (location, skills)
          const { sectionId: processedSectionId, boolStr: processedBoolStr } = processAttributeData(
            suggestion.attribute_name, 
            suggestion.attribute_value, 
            suggestion.location_details
          );
          
          const topic = centralDiscoveryQueue.inferTopicFromSectionId(suggestion.attribute_name) || 'requirements';
          
          // Check if there's an existing item with similar attribute_name or section_id
          const existingItem = existingItems.find(item => 
            (item.topic === topic && 
             (item.attribute_name === suggestion.attribute_name || 
              item.section_name === suggestion.attribute_name ||
              item.bool_str?.includes(suggestion.attribute_name))) ||
            item.section_id === processedSectionId
          );
          
          if (existingItem) {
            // Update existing item by merging bool_str
            const updatedBoolStr = centralDiscoveryQueue.mergeBoolStrings(
              existingItem.bool_str, 
              processedBoolStr
            );
            
            const updatedQueueItem = {
              ...existingItem,
              bool_str: updatedBoolStr,
              section_type: suggestion.must_have_qualifier ? 'Must Have' : 'Nice to Have',
              disco_done: true,
              updated_from_feedback: true,
              updated_at: Date.now()
            };
            
            centralDiscoveryQueue.addQueueItem(updatedQueueItem);
            console.log('Updated existing requirement from suggestion feedback:', updatedQueueItem);
          } else {
            // Create new item with processed section_id
            const queueId = `${topic}_${processedSectionId}_${Date.now()}_${index}`;
            
            const newQueueItem = {
              topic: topic,
              queue_id: queueId,
              section_id: processedSectionId,
              section_name: suggestion.attribute_name,
              section_type: suggestion.must_have_qualifier ? 'Must Have' : 'Nice to Have',
              bool_str: processedBoolStr,
              attribute_name: suggestion.attribute_name,
              attribute_value: suggestion.attribute_value,
              temporal_qualifier: suggestion.temporal_qualifier || '',
              disco_done: true,
              created_from_feedback: true,
              status: 'discovery_done'
            };
            
            centralDiscoveryQueue.addQueueItem(newQueueItem);
            console.log('Added new suggestion from feedback:', newQueueItem);
          }
        }
      });
    }
    
    console.log('Processing selected chips completed:', {
      additions: Array.from(additions),
      removals: Array.from(removals), 
      suggestions: Array.from(suggestions),
      shouldRefreshResults
    });
    
    // Refresh results if requested
    if (shouldRefreshResults && typeof handleRefreshResultsFromQueue === 'function') {
      console.log('Refreshing candidate results with updated requirements...');
      await handleRefreshResultsFromQueue();
    }
  };

  // Popover handlers for matching criteria
  const handlePopoverOpen = (event, criteria) => {
    setPopoverAnchorEl(event.currentTarget);
    setPopoverCriteria(criteria);
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
    setPopoverCriteria([]);
  };

  const isPopoverOpen = Boolean(popoverAnchorEl);

  // Helper functions for parsing matching criteria (simplified from original)
  function parseQueryElements(queryString) {
    if (!queryString) return [];
    
    let cleanQuery = queryString.trim();
    if (cleanQuery.startsWith('(') && cleanQuery.endsWith(')')) {
      cleanQuery = cleanQuery.slice(1, -1);
    }
    
    const elements = cleanQuery
      .split(/\s+(?:OR|AND)\s+/)
      .map(element => element.trim())
      .filter(element => element.length > 0)
      .map(element => {
        return element.replace(/^['"`(]+|['"`)]+$/g, '').trim();
      })
      .filter(element => element.length > 0);
    
    return elements;
  }

  function getReadableName(element, queries) {
    console.log('queries[element]', element, queries );
    // If it's a hex ID, try to look it up
    if (/^[a-f0-9]{24}$/i.test(element) && queries && queries[element]) {
      return queries[element].name || element;
    }
    
    // If it contains a colon, extract the value part
    if (element.includes(':')) {
      const parts = element.split(':');
      if (parts.length > 1) {
        let value = parts[parts.length - 1].trim();
        value = value.replace(/^['"`(]+|['"`)]+$/g, '');
        return value;
      }
    }
    
    return element;
  }

  // Get matching criteria for a candidate
  function getMatchingCriteria(profileId, macroMappingData) {
    if (!macroMappingData || !macroMappingData.queryResults || !macroMappingData.autoMacroMap) {
      return [];
    }

    const queryResult = macroMappingData.queryResults.find(result => result.profile_id === profileId);
    if (!queryResult || !queryResult.macro_ids) {
      return [];
    }

    const allCriteria = [];
    
    queryResult.macro_ids.forEach(macroId => {
      const macroValue = macroMappingData.autoMacroMap[macroId];
      if (!macroValue) return;

      const elements = parseQueryElements(macroValue);
      
      elements.forEach(element => {
        const readableName = getReadableName(element, macroMappingData.queries);
        
        if (readableName && 
            readableName.length > 2 && 
            !readableName.startsWith('profile_type:') &&
            !/^[a-f0-9]{24}$/i.test(readableName)) {
          allCriteria.push(readableName);
        }
      });
    });

    return [...new Set(allCriteria)];
  }

  // Get current visible candidates based on offset
  const visibleCandidates = candidateList.slice(currentCandidateOffset, currentCandidateOffset + 5);
  
  // Handle loading next 5 candidates
  const handleLoadNextCandidates = () => {
    const nextOffset = currentCandidateOffset + 5;
    if (nextOffset < candidateList.length) {
      setCurrentCandidateOffset(nextOffset);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: '#FFFFFF',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '40px',
          paddingBottom: '120px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F9FAFB',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E5E7EB',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#D1D5DB',
          }
        }}
      >
        {/* Chat Wrapper */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '32px',
            padding: '24px 10px 10px',
          }}
        >
        {/* AI Message */}
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            width: '616px',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              width: 20,
              minWidth: 20,
              height: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '4px',
            }}
          >
            <AIIcon size={40} />
          </Box>
          
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <Paper
              sx={{
                padding: '0px 8px',
                borderRadius: '10px 10px 10px 0px',
                backgroundColor: '#FFFFFF',
                border: 'none',
                boxShadow: 'none',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '16px',
                  lineHeight: '1.375em',
                  color: '#101828',
                }}
              >
                Let's review a few candidate matches to make sure we're on the same page.
                <br />
                I'll need you to approve 5 matching candidates before we can kick off outreach 🚀
                <br />
                Ready when you are! 👀✅
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* Candidate List */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '616px',
            position: 'relative',
          }}
        >
          {/* Loading overlay for profile refresh */}
          {isLoadingProfiles && candidateList.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                borderRadius: '12px',
                zIndex: 10,
              }}
            >
              <CircularProgress 
                size={32} 
                sx={{ color: '#4599FA' }}
              />
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#667085',
                  textAlign: 'center',
                }}
              >
                Refreshing profiles with updated requirements...
              </Typography>
            </Box>
          )}
          
          {/* Show loading cards if profiles are being fetched for the first time */}
          {isLoadingProfiles && candidateList.length === 0 ? (
            [...Array(5)].map((_, index) => (
              <LoadingCandidateCard key={`loading-${index}`} />
            ))
          ) : candidateList.length === 0 ? (
            /* No profiles and not loading - show manual fetch option */
            <Paper
              sx={{
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #DCDFEA',
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '16px',
                  color: '#667085',
                  textAlign: 'center',
                }}
              >
                No candidate profiles loaded yet. Make sure you've completed your requirements first.
              </Typography>
              <Button
                variant="contained"
                onClick={handleFetchProfiles}
                disabled={isLoadingProfiles}
                sx={{
                  backgroundColor: '#4599FA',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                }}
              >
                {isLoadingProfiles ? <CircularProgress size={20} /> : 'Load Candidate Profiles'}
              </Button>
            </Paper>
          ) : (
            /* Show candidate cards */
            visibleCandidates.map((candidate) => {
            const isApproved = approvedCandidates.has(candidate.id);
            const isRejected = rejectedCandidates.has(candidate.id);
            const matchingCriteria = getMatchingCriteria(candidate.id, macroMappingData);
            
            return (
              <Paper
                key={candidate.id}
                sx={{
                  padding: '24px 16px',
                  borderRadius: '12px',
                  border: '1px solid #DCDFEA',
                  backgroundColor: '#FFFFFF',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {/* Candidate Header */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Candidate Info */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '16px',
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'flex-start',
                      }}
                    >
                      {/* Avatar */}
                      <Avatar
                        sx={{
                          width: '66px',
                          height: '66px',
                          backgroundColor: '#C5E0FF',
                        }}
                        src={candidate.profile_pic_url}
                      >
                        {candidate.first_name?.[0]}{candidate.last_name?.[0]}
                      </Avatar>
                      
                      {/* Basic Info */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                          width: '440px',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1px',
                          }}
                        >
                          {/* Name */}
                          <Typography
                            onClick={() => handleCandidateNameClick(candidate)}
                            sx={{
                              fontFamily: 'Roboto',
                              fontSize: '18px',
                              fontWeight: 500,
                              lineHeight: '1.333em',
                              color: '#6750A4',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              '&:hover': {
                                color: '#5a3d99',
                              },
                            }}
                          >
                            {candidate.first_name} {candidate.last_name}
                          </Typography>
                          
                          {/* Current Job */}
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontSize: '14px',
                              lineHeight: '1.429em',
                              color: '#465366',
                            }}
                          >
                            {candidate.title} at {candidate.company}
                          </Typography>
                          
                          {/* Location */}
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontSize: '14px',
                              lineHeight: '1.429em',
                              color: '#667085',
                            }}
                          >
                            {candidate.location}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <IconButton
                      onClick={() => handleThumbsDown(candidate)}
                      sx={{
                        width: '32px',
                        height: '32px',
                        border: '1px solid #DCDFEA',
                        borderRadius: '8px',
                        backgroundColor: isRejected ? '#FEF3F2' : '#FFFFFF',
                        '&:hover': {
                          backgroundColor: '#FEF3F2',
                        },
                      }}
                    >
                      <ThumbDown
                        sx={{
                          fontSize: '18px',
                          color: isRejected ? '#B42318' : '#465366',
                        }}
                      />
                    </IconButton>
                    
                    <IconButton
                      onClick={() => handleApproval(candidate.id, true)}
                      sx={{
                        width: '32px',
                        height: '32px',
                        border: '1px solid #DCDFEA',
                        borderRadius: '8px',
                        backgroundColor: isApproved ? '#E0FFE9' : '#FFFFFF',
                        '&:hover': {
                          backgroundColor: '#E0FFE9',
                        },
                      }}
                    >
                      <ThumbUp
                        sx={{
                          fontSize: '18px',
                          color: isApproved ? '#207C39' : '#465366',
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
                
                {/* Matching Criteria Chips */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexWrap: 'wrap',
                  }}
                >
                  {matchingCriteria.slice(0, 4).map((criteria, index) => (
                    <Chip
                      key={index}
                      icon={<CheckCircle sx={{ fontSize: '14px', color: '#207C39' }} />}
                      label={unescape(criteria)}
                      sx={{
                        backgroundColor: '#E0FFE9',
                        color: '#207C39',
                        fontSize: '12px',
                        fontFamily: 'Roboto',
                        height: '24px',
                        borderRadius: '16px',
                        '& .MuiChip-icon': {
                          fontSize: '14px',
                          color: '#207C39',
                        },
                      }}
                    />
                  ))}
                  {matchingCriteria.length > 4 && (
                    <Chip
                      label={`+${matchingCriteria.length - 4} more`}
                      onMouseEnter={(event) => handlePopoverOpen(event, matchingCriteria.slice(4))}
                      onMouseLeave={handlePopoverClose}
                      sx={{
                        backgroundColor: '#F3F4F6',
                        color: '#6B7280',
                        fontSize: '12px',
                        fontFamily: 'Roboto',
                        height: '24px',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#E5E7EB',
                        },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            );
          }))}
        </Box>

        {/* Load Next Candidates Button */}
        {currentCandidateOffset + 5 < candidateList.length && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '616px',
              marginTop: '20px',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleLoadNextCandidates}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontFamily: 'Roboto',
                fontWeight: 500,
                borderColor: '#6750A4',
                color: '#6750A4',
                backgroundColor: '#FFFFFF',
                padding: '12px 24px',
                fontSize: '14px',
                '&:hover': {
                  borderColor: '#5a3d99',
                  backgroundColor: '#f0f7ff',
                  color: '#5a3d99',
                },
              }}
            >
              Load Next Candidates ({Math.min(5, candidateList.length - currentCandidateOffset - 5)} more)
            </Button>
          </Box>
        )}
        </Box>
      </Box>

      {/* Progress Indicator */}
      <Paper
        sx={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #DCDFEA',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '1.429em',
            color: '#101828',
          }}
        >
          Approve 5 to continue
        </Typography>
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {[...Array(5)].map((_, index) => (
            <Box
              key={index}
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: index < approvedCandidates.size ? '#00B093' : '#F3F5F8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {index < approvedCandidates.size && (
                <ThumbUp sx={{ fontSize: '12px', color: '#FFFFFF' }} />
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Candidate Profile Sidebar */}
      {sidebarOpen && selectedCandidate && (
        <CandidateProfileSidebar
          candidate={selectedCandidate}
          profileId={selectedCandidateId}
          open={sidebarOpen}
          onClose={handleSidebarClose}
          macroMappingData={macroMappingData}
        />
      )}

      {/* Thumbs Down Reason Modal */}
      <Dialog
        open={showReasonModal}
        onClose={handleReasonCancel}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '0px',
            boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
            width: '560px',
            margin: '0px',
          }
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'stretch',
            alignItems: 'stretch',
            alignSelf: 'stretch',
            padding: '24px 32px',
            borderBottom: '1px solid #EAECF0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                width: '100%',
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '8px',
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #DCDFEA',
                  borderRadius: '6px',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Material Icons Round',
                    fontWeight: 400,
                    fontSize: '18px',
                    lineHeight: '1em',
                    color: '#465366',
                  }}
                >
                  edit_note
                </Typography>
              </Box>
              
              {/* Text */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                }}
              >
                {/* Title */}
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 500,
                    fontSize: '18px',
                    lineHeight: '24px',
                    color: '#313131',
                  }}
                >
                  Candidate Feedback
                </Typography>
                
                {/* Subtitle */}
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#465366',
                  }}
                >
                  Help us understand what didn't quite fit with {selectedCandidateForReason?.name || 'this candidate'}
                </Typography>
              </Box>
            </Box>
            
            {/* Close Button */}
            <IconButton
              onClick={handleReasonCancel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 12px',
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                color: '#667085',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Material Icons Round',
                  fontWeight: 400,
                  fontSize: '20px',
                  lineHeight: '1em',
                  color: '#667085',
                }}
              >
                close
              </Typography>
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'stretch',
            gap: '20px',
            padding: '32px',
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch',
              gap: '8px',
              backgroundColor: '#FFFFFF',
            }}
          >
            {/* Feedback Label */}
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#101828',
              }}
            >
              Feedback:
            </Typography>
            
            {/* Input */}
            <TextField
              autoFocus
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              placeholder="e.g., Missing required Python experience, location doesn't work, etc."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #DCDFEA',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: '#DCDFEA',
                  },
                  '&.Mui-focused': {
                    borderColor: '#DCDFEA',
                  },
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#667085',
                  padding: '0px',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#667085',
                  opacity: 1,
                }
              }}
            />
          </Box>

          {/* Get Suggestions Button */}
          {reason.trim() && !isLoadingFeedback && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                onClick={() => fetchICPSuggestions(reason.trim())}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  borderColor: '#4599FA',
                  color: '#4599FA',
                  '&:hover': {
                    backgroundColor: '#F0F7FF',
                    borderColor: '#4599FA',
                  },
                }}
              >
                {icpEditResponse ? 'Refresh AI Suggestions' : 'Get AI Suggestions'}
              </Button>
            </Box>
          )}

          {/* Loading State */}
          {isLoadingFeedback && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
              }}
            >
              <CircularProgress size={16} />
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: '#667085',
                }}
              >
                Getting suggestions...
              </Typography>
            </Box>
          )}

          {/* ICP Edit Response Chips */}
          {icpEditResponse && !isLoadingFeedback && (
            <>
              {/* Additions */}
              {icpEditResponse.additions && icpEditResponse.additions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#059669',
                      mb: 1,
                    }}
                  >
                    Suggested Additions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {icpEditResponse.additions.map((addition, index) => (
                      <Chip
                        key={`addition-${index}`}
                        label={`${addition.attribute_name}: ${addition.attribute_value}`}
                        variant={selectedChips.additions.has(index) ? 'filled' : 'outlined'}
                        onClick={() => handleChipToggle('additions', index)}
                        sx={{
                          backgroundColor: selectedChips.additions.has(index) ? '#DCFCE7' : '#FFFFFF',
                          color: selectedChips.additions.has(index) ? '#059669' : '#6B7280',
                          borderColor: selectedChips.additions.has(index) ? '#059669' : '#DCDFEA',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#DCFCE7',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Removals */}
              {icpEditResponse.removals && icpEditResponse.removals.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#DC2626',
                      mb: 1,
                    }}
                  >
                    Suggested Removals:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {icpEditResponse.removals.map((removal, index) => (
                      <Chip
                        key={`removal-${index}`}
                        label={`${removal.attribute_name}: ${removal.attribute_value}`}
                        variant={selectedChips.removals.has(index) ? 'filled' : 'outlined'}
                        onClick={() => handleChipToggle('removals', index)}
                        sx={{
                          backgroundColor: selectedChips.removals.has(index) ? '#FEE2E2' : '#FFFFFF',
                          color: selectedChips.removals.has(index) ? '#DC2626' : '#6B7280',
                          borderColor: selectedChips.removals.has(index) ? '#DC2626' : '#DCDFEA',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#FEE2E2',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Suggestions */}
              {icpEditResponse.suggestions && icpEditResponse.suggestions.actions && icpEditResponse.suggestions.actions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: '#2563EB',
                      mb: 1,
                    }}
                  >
                    AI Suggestions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {icpEditResponse.suggestions.actions.map((suggestion, index) => (
                      <Chip
                        key={`suggestion-${index}`}
                        label={`${suggestion.attribute_name}: ${suggestion.attribute_value}`}
                        variant={selectedChips.suggestions.has(index) ? 'filled' : 'outlined'}
                        onClick={() => handleChipToggle('suggestions', index)}
                        sx={{
                          backgroundColor: selectedChips.suggestions.has(index) ? '#DBEAFE' : '#FFFFFF',
                          color: selectedChips.suggestions.has(index) ? '#2563EB' : '#6B7280',
                          borderColor: selectedChips.suggestions.has(index) ? '#2563EB' : '#DCDFEA',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#DBEAFE',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Modal Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'stretch',
            alignItems: 'stretch',
            alignSelf: 'stretch',
            padding: '0px 32px 24px 32px',
            borderTop: '1px solid #EAECF0',
            pt: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              width: '100%',
            }}
          >
            {/* Cancel Button */}
            <Button
              onClick={handleReasonCancel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #DCDFEA',
                borderRadius: '6px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                  borderColor: '#DCDFEA',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  textAlign: 'center',
                  color: '#101828',
                }}
              >
                Cancel
              </Typography>
            </Button>
            
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {/* Update & Continue Button */}
              <Button
                onClick={handleUpdateAndContinue}
                disabled={!reason.trim()}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #4599FA',
                  color: '#4599FA',
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#F0F7FF',
                  },
                  '&:disabled': {
                    backgroundColor: '#F2F4F7',
                    borderColor: '#D1D5DB',
                    color: '#667085',
                  }
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    textAlign: 'center',
                    color: 'inherit',
                  }}
                >
                  Update & Continue
                </Typography>
              </Button>
              
              {/* Update & Refresh Button */}
              <Button
                onClick={handleUpdateAndRefresh}
                disabled={!reason.trim()}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  backgroundColor: '#4599FA',
                  borderRadius: '6px',
                  textTransform: 'none',
                  border: 'none',
                  '&:hover': {
                    backgroundColor: '#3A7BD8',
                  },
                  '&:disabled': {
                    backgroundColor: '#F2F4F7',
                    color: '#667085',
                  }
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    textAlign: 'center',
                    color: reason.trim() ? '#FFFFFF' : '#667085',
                  }}
                >
                  Update & Refresh Profile Results
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Matching Criteria Popover */}
      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            maxWidth: '300px',
            pointerEvents: 'auto',
          }
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '8px',
          }}
        >
          All Matching Attributes
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {popoverCriteria.map((criteria, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <CheckCircle sx={{ fontSize: '14px', color: '#207C39' }} />
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontSize: '12px',
                  color: '#207C39',
                }}
              >
                {criteria}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default ReviewMatchesTabV2;
